import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Link,
} from "@mui/material";
import { Lock, Email } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../Services/axiosConfig";
import ThemeProviderWrapper from "../components/ThemeProviderWrapper";
import FeedbackButton from "../components/FeedbackButton";
import { ThemeModeProvider } from "../components/ThemeModeContext";
import { useTheme } from "@mui/material/styles";

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleLogin = async () => {
    setLoading(true);
    setErrorMessage("");

    try {
      const response = await axiosInstance.post("login/", { email, password });

      if (response.data.status === "success") {
        localStorage.setItem("authToken", response.data.token);
        if (response.data.is_company) {
          if (response.data.role)
          {
            localStorage.setItem("role", response.data.role);
          }
          else
          {
            localStorage.setItem("role", "owner");
          }
          window.location.href = "/company-dashboard/" + response.data.company_id;
        } else {
          window.location.href = "/user/home";
        }
      } else {
        setErrorMessage(response.data.message);
      }
    } catch (error) {
      setErrorMessage("Um erro ocorreu. Por favor, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeModeProvider>
      <ThemeProviderWrapper>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            p: 3,
            overflow: "hidden",
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
          }}
        >
          {/* Bolas decorativas com animação */}
          <Box
            sx={{
              position: "absolute",
              width: 300,
              height: 300,
              borderRadius: "50%",
              background: theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
              top: -50,
              left: -50,
              animation: "move-1 10s infinite alternate ease-in-out",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 400,
              height: 400,
              borderRadius: "50%",
              background: theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.1)",
              bottom: -100,
              right: -100,
              animation: "move-2 12s infinite alternate ease-in-out",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: theme.palette.mode === "light"
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(0, 0, 0, 0.15)",
              bottom: 50,
              left: 50,
              animation: "move-3 14s infinite alternate ease-in-out",
            }}
          />

          {/* Login Box */}
          <Box
            sx={{
              zIndex: 2,
              width: "90%",
              maxWidth: 500,
              bgcolor: "background.paper",
              p: 4,
              borderRadius: 3,
              boxShadow: 4,
              textAlign: "center",
            }}
          >
            {/* Título */}
            <Typography variant="h4" gutterBottom>
              Bem-vindo de volta!
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Faça login para continuar
            </Typography>

            {/* Mensagem de Erro */}
            {errorMessage && (
              <Typography
                variant="body2"
                color="error"
                align="center"
                gutterBottom
              >
                {errorMessage}
              </Typography>
            )}

            {/* Email */}
            <Box mb={2} display="flex" alignItems="center">
              <Email sx={{ color: "text.secondary", mr: 1 }} />
              <TextField
                label="E-mail"
                variant="outlined"
                fullWidth
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@domain.com"
              />
            </Box>

            {/* Senha */}
            <Box mb={2} display="flex" alignItems="center">
              <Lock sx={{ color: "text.secondary", mr: 1 }} />
              <TextField
                label="Senha"
                type="password"
                variant="outlined"
                fullWidth
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your secure password"
              />
            </Box>

            {/* Botão de Login */}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleLogin}
              disabled={loading}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
              sx={{
                textTransform: "uppercase",
                fontWeight: "bold",
                boxShadow: 2,
                "&:hover": {
                  boxShadow: 4,
                  transform: "scale(1.01)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {loading ? "Logging in..." : "Login"}
            </Button>

            {/* Links para Registro */}
            <Box mt={3}>
              <Typography variant="body2" gutterBottom>
                Novo por aqui? 
              </Typography>
              <Box display="flex" justifyContent="center" gap={2}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => navigate("/register")}
                  sx={{ textDecoration: "none", fontWeight: "bold" }}
                >
                  SE REGISTRE!
                </Link>
              </Box>
            </Box>
          </Box>

          <FeedbackButton />

          {/* Keyframes para animação */}
          <style>
            {`
              @keyframes move-1 {
                0% {
                  transform: translate(0, 0);
                }
                100% {
                  transform: translate(50px, -50px);
                }
              }
              @keyframes move-2 {
                0% {
                  transform: translate(0, 0);
                }
                100% {
                  transform: translate(-50px, 50px);
                }
              }
              @keyframes move-3 {
                0% {
                  transform: translate(0, 0);
                }
                100% {
                  transform: translate(30px, -30px);
                }
              }
            `}
          </style>
        </Box>
      </ThemeProviderWrapper>
    </ThemeModeProvider>
  );
};

export default LoginPage;
